import { StatusDocumentUpload } from '@/components/define-document-upload/define-document-upload.config';
import {
	ALL_DOCUMENT_OPTIONS,
	ApplicantTypeEnum,
	DocumentTypeEnum,
	FileTypeEnum,
	FileTypeLabelEnum,
	PageTypeEnum,
} from '@/constant';
import {
	TDocumentDefineUploadItem,
	TDocumentUploadDefineItem,
	TGetDocumentUploadListResponse,
	TMappingDocumentItem,
	TRelationDocumentInfo,
} from '@/services';

import { formatDate, randomId } from 'utils';

import { includes, keys } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';

// Types for the helper functions
type TBaseItem = {
	id: number;
	fileList: TDocumentUploadDefineItem;
	fileName: string;
	documentType: DocumentTypeEnum;
	hasDocument: boolean;
	documentName: React.ReactNode;
	refTableId: number;
	refTableName: PageTypeEnum;
	typeName: string;
	hidden: boolean;
	statusDocumentUpload: StatusDocumentUpload;
	mappingList: TMappingDocumentItem[];
	registDt?: string;
	modified?: boolean;
};

// Document types that should not be doubled when franchise key exists and mappingList is present
export const FRANCHISE_EXCLUDED_DOCUMENT_TYPES: DocumentTypeEnum[] = [
	DocumentTypeEnum.C_19, // 체류기간 연장 추천 신청서 (Stay Extension Recommendation Application)
	DocumentTypeEnum.C_59, // 고용변동신고서(근무처변경) (Employment Change Report - Workplace Change)
	DocumentTypeEnum.C_63, // 고용변동신고서(무단이탈) (Employment Change Report - Unauthorized Leave)
];

const RECOMMENDER_SPECIAL_DOCUMENT_TYPES = {
	[DocumentTypeEnum.C_37]: ApplicantTypeEnum.FAMILY_RELATIVES,
	[DocumentTypeEnum.C_74]: ApplicantTypeEnum.D2_STUDENT_PARENT_INVITATION,
} as Record<DocumentTypeEnum, ApplicantTypeEnum>;

export const TARGET_PAGES = [
	PageTypeEnum.APPLICANT,
	PageTypeEnum.FRANCHISE,
	PageTypeEnum.LODGING,
	PageTypeEnum.RECOMMENDER,
];

export const DOCUMENT_DEFAULT = [
	DocumentTypeEnum.C_36,
	DocumentTypeEnum.C_28,
	DocumentTypeEnum.C_27,
	DocumentTypeEnum.C_31,
	DocumentTypeEnum.C_30,
	DocumentTypeEnum.C_35,
	DocumentTypeEnum.C_29,
	DocumentTypeEnum.C_34,
	DocumentTypeEnum.C_33,
	DocumentTypeEnum.C_26,
	DocumentTypeEnum.C_51,
	DocumentTypeEnum.C_40,
	DocumentTypeEnum.C_62,
	DocumentTypeEnum.C_12,
	DocumentTypeEnum.C_20,
	DocumentTypeEnum.C_69,
	DocumentTypeEnum.C_53,
];

const getDocumentInfo = (
	item: TDocumentUploadDefineItem
): {
	label: React.ReactNode;
	hasDocument: boolean;
	parents: PageTypeEnum[];
	hasApplicantMapping: boolean;
	hasC54: boolean;
} => {
	const option = ALL_DOCUMENT_OPTIONS.find(
		({ value }) => value === item.documentType
	);

	const pageLabel = item.refTableName
		? option?.labelByPage?.[item.refTableName]
		: undefined;

	return {
		label: pageLabel ?? option?.label ?? '',
		hasDocument: option?.hasDocument || false,
		parents: option?.hasDocument ? option?.parents : TARGET_PAGES,
		hasApplicantMapping:
			item.refTableName !== PageTypeEnum.APPLICANT
				? option?.hasDocument
					? option?.parents?.some((parent) => parent === PageTypeEnum.APPLICANT)
					: false
				: true,
		hasC54: item.documentType === DocumentTypeEnum.C_54,
	};
};

export const createMapping = (
	refTableName: PageTypeEnum,
	refTableId: number | null
): TMappingDocumentItem => ({
	fileId: null as unknown as number,
	id: randomId(),
	refTableId: refTableId ?? (null as unknown as number),
	refTableName: refTableName?.toUpperCase() as PageTypeEnum,
	registDt: '',
	status: StatusDocumentUpload.ADD,
});

export const formatDefineDocumentUploadList = (
	input: TGetDocumentUploadListResponse,
	relationDocumentInfo?: TRelationDocumentInfo
): TBaseItem[] => {
	const result = input.flatMap(
		(item: TDocumentDefineUploadItem, index: number) => {
			const items = processSingleItem(item, index, relationDocumentInfo);
			return items;
		}
	);

	const sortedItems = result.sort((a, b) => {
		const aName = a.documentName?.toString() || '';
		const bName = b.documentName?.toString() || '';
		return aName.localeCompare(bName, 'ko');
	});

	return sortedItems;
};

const processSingleItem = (
	item: TDocumentDefineUploadItem,
	index: number,
	relationDocumentInfo?: TRelationDocumentInfo
): TBaseItem[] => {
	if (isNilOrEmpty(item.fileList)) {
		return duplicateItemsForArrayValues({
			relationDocumentInfo,
			item: item as unknown as TDocumentUploadDefineItem,
		});
	}

	return item.fileList.flatMap((file, fileIndex, fileArray) => {
		return duplicateItemsForArrayValues({
			relationDocumentInfo,
			item: file,
			index: fileIndex,
			array: fileArray,
		});
	});
};

const duplicateItemsForArrayValues = ({
	relationDocumentInfo,
	item,
	index = -1,
	array = [],
}: {
	relationDocumentInfo?: TRelationDocumentInfo;
	item: TDocumentUploadDefineItem;
	index?: number;
	array?: TDocumentUploadDefineItem[];
}): TBaseItem[] => {
	const { label, hasDocument, parents, hasApplicantMapping, hasC54 } =
		getDocumentInfo(item);

	const initBaseItem: TBaseItem = {
		id: randomId(),
		fileList: {} as TDocumentUploadDefineItem,
		fileName: '',
		documentType: item.documentType as DocumentTypeEnum,
		hasDocument,
		documentName: label,
		refTableId: item?.refTableId as number,
		refTableName: item.refTableName as PageTypeEnum,
		typeName: '-',
		hidden: false,
		statusDocumentUpload: StatusDocumentUpload.ADD,
		mappingList: [],
	};

	let baseItem = {} as TBaseItem;

	if (item && item?.id) {
		const fileItem = item as TDocumentUploadDefineItem;
		baseItem = {
			...initBaseItem,
			id: fileItem.id,
			typeName:
				fileItem.type === FileTypeEnum.UPLOAD
					? FileTypeLabelEnum.UPLOAD
					: FileTypeLabelEnum.FORM,
			fileList: { ...fileItem },
			fileName: fileItem.name,
			mappingList: fileItem.mappingList || [],
			statusDocumentUpload: StatusDocumentUpload.UPDATE,
			registDt: formatDate(fileItem.registDt),
			modified: fileItem?.modified,
		};
	} else {
		baseItem = {
			...initBaseItem,
		};
	}

	if ((!hasDocument || !hasApplicantMapping) && !hasC54) {
		if (item && item?.id) {
			baseItem = {
				...baseItem,
				mappingList:
					baseItem.mappingList?.length > 1
						? baseItem.mappingList
						: [
								...baseItem.mappingList,
								createMapping('' as PageTypeEnum, null),
							],
			};
		} else {
			baseItem = {
				...baseItem,
				mappingList: [createMapping('' as PageTypeEnum, null)],
			};
		}
		return [baseItem];
	}

	const isLastFile = index === array?.length - 1;

	const { applicant, franchise, lodging, recommender } =
		relationDocumentInfo || {};

	const duplicatedItems: TBaseItem[] = [baseItem];

	const mappingSet = (list: TMappingDocumentItem[]) =>
		new Set(
			list.map((ml) => `${ml.refTableName?.toUpperCase()}-${ml.refTableId}`)
		);

	const pushIfNotExist = (
		list: TMappingDocumentItem[] = [],
		refTableName: PageTypeEnum,
		refTableId: number,
		type: 'push' | 'unshift' = 'push'
	) => {
		if (
			!list.some(
				(ml) => ml.refTableName === refTableName && ml.refTableId === refTableId
			)
		) {
			if (type === 'push') {
				list.push(createMapping(refTableName, refTableId));
			} else {
				list.unshift(createMapping(refTableName, refTableId));
			}
		}
	};

	// Handle applicant logic
	if (applicant) {
		if (item?.id) {
			if (isLastFile) {
				const existingSet = mappingSet(
					array.flatMap((item) => item.mappingList)
				);

				const hasFranchiseIdInMappingList = existingSet?.has(
					`${PageTypeEnum.FRANCHISE}-${applicant.franchise?.id}`
				);

				let hasRegistFileBeforeAssignmentDt = false;

				if (hasFranchiseIdInMappingList) {
					array?.forEach((_) => {
						if (hasRegistFileBeforeAssignmentDt) return;
						if (
							_?.mappingList?.some(
								(ml) =>
									ml.refTableId === applicant.franchise?.id &&
									ml.refTableName === PageTypeEnum.FRANCHISE
							)
						) {
							if (
								applicant.franchise?.assignmentRegistDt &&
								_.registDt &&
								Number(applicant.franchise?.assignmentRegistDt) >
									Number(_.registDt)
							) {
								hasRegistFileBeforeAssignmentDt = true;
							}
						}
					});
				}

				const hasFranchise =
					applicant.franchise?.id &&
					includes(PageTypeEnum.FRANCHISE, parents) &&
					(!hasFranchiseIdInMappingList || hasRegistFileBeforeAssignmentDt);

				const hasLodging =
					applicant.lodging &&
					includes(PageTypeEnum.LODGING, parents) &&
					!existingSet.has(`${PageTypeEnum.LODGING}-${applicant.lodging}`);
				const hasRecommender =
					applicant.recommender &&
					includes(PageTypeEnum.RECOMMENDER, parents) &&
					!existingSet.has(
						`${PageTypeEnum.RECOMMENDER}-${applicant.recommender}`
					);

				if (
					RECOMMENDER_SPECIAL_DOCUMENT_TYPES[item.documentType]
						? hasRecommender
						: hasFranchise || hasLodging || hasRecommender
				) {
					const mappingList: TMappingDocumentItem[] = [];

					if (applicant.franchise?.id) {
						pushIfNotExist(
							mappingList,
							PageTypeEnum.FRANCHISE,
							applicant.franchise.id
						);
					}
					if (applicant.lodging) {
						pushIfNotExist(
							mappingList,
							PageTypeEnum.LODGING,
							applicant.lodging
						);
					}
					if (applicant.recommender) {
						pushIfNotExist(
							mappingList,
							PageTypeEnum.RECOMMENDER,
							applicant.recommender
						);
					}

					duplicatedItems.push({
						...initBaseItem,
						id: randomId(),
						mappingList,
					});
				}
			}
		} else {
			Object.entries(applicant).forEach(([key, value]: any) => {
				if (key !== 'applicant' && value) {
					pushIfNotExist(
						duplicatedItems[0].mappingList,
						key.toUpperCase() as PageTypeEnum,
						(value?.id || value) as number
					);
				}
			});
		}
	}

	// Handle franchise logic
	if (franchise?.lodgingApplicant?.length && !hasC54) {
		const existingFileListMappingSet = mappingSet(
			array.flatMap((item) => item.mappingList)
		);
		const existingSet = mappingSet(baseItem.mappingList);
		const baseMappingList = duplicatedItems[0].mappingList;

		const isExcludedDocumentType = FRANCHISE_EXCLUDED_DOCUMENT_TYPES.includes(
			item?.documentType as DocumentTypeEnum
		);

		franchise.lodgingApplicant.forEach(({ applicant, lodging }, index, arr) => {
			const applicantKey = `${PageTypeEnum.APPLICANT}-${applicant}`;

			if (!baseMappingList?.length) {
				if (applicant) {
					pushIfNotExist(baseMappingList, PageTypeEnum.APPLICANT, applicant);
				}
				if (lodging) {
					pushIfNotExist(
						baseMappingList,
						PageTypeEnum.LODGING,
						lodging as number
					);
				}
				return;
			} else {
				if (existingSet.has(applicantKey)) {
					if (lodging) {
						pushIfNotExist(
							baseMappingList,
							PageTypeEnum.LODGING,
							lodging as number
						);
					}
					return;
				}
			}
			const mappingList: TMappingDocumentItem[] = [];

			if (applicant) {
				pushIfNotExist(mappingList, PageTypeEnum.APPLICANT, applicant);
			}

			if (lodging) {
				pushIfNotExist(mappingList, PageTypeEnum.LODGING, lodging);
			}

			if (isExcludedDocumentType) {
				baseMappingList.push(...mappingList);
				return;
			}

			if (isLastFile && !existingFileListMappingSet.has(applicantKey)) {
				duplicatedItems.push({
					...initBaseItem,
					id: randomId(),
					mappingList,
				});
			}
		});

		if (isExcludedDocumentType) {
			const seen = mappingSet([]);
			for (let i = baseMappingList.length - 1; i >= 0; i--) {
				const item = baseMappingList[i];
				const duplicateKey = `${item.refTableName}-${item.refTableId}`;
				if (seen.has(duplicateKey)) {
					baseMappingList.splice(i, 1);
				} else {
					seen.add(duplicateKey);
				}
			}
		}
	}
	if (franchise?.lodging?.length && hasC54) {
		const baseMappingList = duplicatedItems[0].mappingList;

		const existingFileListMappingSet = mappingSet(
			array.flatMap((item) => item.mappingList)
		);

		franchise.lodging.forEach(({ id }, index, arr) => {
			if (!baseMappingList?.length) {
				pushIfNotExist(baseMappingList, PageTypeEnum.LODGING, id);
				return;
			}

			if (
				isLastFile &&
				!existingFileListMappingSet.has(`${PageTypeEnum.LODGING}-${id}`)
			) {
				duplicatedItems.push({
					...initBaseItem,
					id: randomId(),
					mappingList: [createMapping(PageTypeEnum.LODGING, id)],
				});
			}
		});
	}

	// Handle lodging logic
	if (lodging && !hasC54) {
		const baseMappingList = duplicatedItems[0].mappingList;
		if (lodging?.applicant?.length) {
			const existingSet = mappingSet(baseItem.mappingList);
			const existingFileListMappingSet = mappingSet(
				array.flatMap((item) => item.mappingList)
			);

			lodging.applicant.forEach((applicantId, index, arr) => {
				const applicantKey = `${PageTypeEnum.APPLICANT}-${applicantId}`;
				const isHasApplicant = existingSet.has(applicantKey);

				const isItemDuplicate =
					isLastFile && !existingFileListMappingSet.has(applicantKey);

				const mappingList: TMappingDocumentItem[] = [];

				if (!isHasApplicant) {
					pushIfNotExist(mappingList, PageTypeEnum.APPLICANT, applicantId);
				}

				if (
					lodging?.franchise &&
					(isItemDuplicate
						? true
						: !existingSet.has(
								`${PageTypeEnum.FRANCHISE}-${lodging.franchise}`
							))
				) {
					pushIfNotExist(
						mappingList,
						PageTypeEnum.FRANCHISE,
						lodging.franchise
					);
				}
				if (
					lodging?.recommender &&
					(isItemDuplicate
						? true
						: !existingSet.has(
								`${PageTypeEnum.RECOMMENDER}-${lodging.recommender}`
							))
				) {
					pushIfNotExist(
						mappingList,
						PageTypeEnum.RECOMMENDER,
						lodging.recommender
					);
				}

				if (!baseMappingList?.length) {
					baseMappingList.push(...mappingList);
				} else {
					if (isItemDuplicate) {
						duplicatedItems.push({
							...initBaseItem,
							id: randomId(),
							mappingList,
						});
					}
				}
			});
		} else if (!item?.id) {
			const mappingList: TMappingDocumentItem[] = [];

			if (lodging?.franchise) {
				pushIfNotExist(mappingList, PageTypeEnum.FRANCHISE, lodging.franchise);
			}
			if (lodging?.recommender) {
				pushIfNotExist(
					mappingList,
					PageTypeEnum.RECOMMENDER,
					lodging.recommender
				);
			}
			if (mappingList.length) {
				baseMappingList.push(...mappingList);
			}
		}
	}

	if (lodging && hasC54) {
		const mappingList: TMappingDocumentItem[] = [];
		const existingFileListMappingSet = mappingSet(
			array.flatMap((item) => item.mappingList)
		);

		const hasRecommender =
			lodging?.recommender &&
			!existingFileListMappingSet.has(
				`${PageTypeEnum.RECOMMENDER}-${lodging.recommender}`
			);
		const hasFranchise =
			lodging?.franchise &&
			!existingFileListMappingSet.has(
				`${PageTypeEnum.FRANCHISE}-${lodging.franchise}`
			);

		if (hasRecommender) {
			pushIfNotExist(
				mappingList,
				PageTypeEnum.RECOMMENDER,
				lodging.recommender as number
			);
		}

		if (hasFranchise) {
			pushIfNotExist(
				mappingList,
				PageTypeEnum.FRANCHISE,
				lodging.franchise as number
			);
		}

		if (!baseItem.mappingList?.length) {
			baseItem.mappingList.push(...mappingList);
		} else if (hasRecommender || hasFranchise) {
			duplicatedItems.push({
				...initBaseItem,
				id: randomId(),
				mappingList,
			});
		}
	}

	// Handle recommender
	if (recommender?.applicant?.length && !hasC54) {
		const existingSet = mappingSet([]);
		const existingSpecialSet = mappingSet([]);
		const existingFileListMappingSet = mappingSet(
			array.flatMap((item) => item.mappingList)
		);
		const baseMappingList = duplicatedItems[0].mappingList;

		const isSpecialDocumentType = includes(
			item?.documentType as DocumentTypeEnum,
			keys(RECOMMENDER_SPECIAL_DOCUMENT_TYPES)
		);

		(isSpecialDocumentType
			? recommender.applicant?.filter(
					({ id, applicationType }) =>
						RECOMMENDER_SPECIAL_DOCUMENT_TYPES[
							item?.documentType as DocumentTypeEnum
						] === applicationType
				)
			: recommender.applicant
		)?.forEach(({ id }, index, arr) => {
			if (!id) return;
			const applicantKey = `${PageTypeEnum.APPLICANT}-${id}`;

			if (!baseItem.mappingList?.length) {
				baseMappingList.push(...[createMapping(PageTypeEnum.APPLICANT, id)]);
			} else {
				if (!existingFileListMappingSet.has(applicantKey)) {
					if (isSpecialDocumentType) {
						existingSpecialSet.add(applicantKey);
						return;
					}

					existingSet.add(applicantKey);
				}
			}
		});

		if (isLastFile) {
			(isSpecialDocumentType ? existingSpecialSet : existingSet)?.forEach(
				(key) => {
					const applicantId = Number(key.split('-')[1]);

					duplicatedItems.push({
						...initBaseItem,
						id: randomId(),
						mappingList: [createMapping(PageTypeEnum.APPLICANT, applicantId)],
					});
				}
			);
		}
	}
	if (recommender?.lodging?.length && hasC54) {
		const baseMappingList = duplicatedItems[0].mappingList;

		const existingFileListMappingSet = mappingSet(
			array.flatMap((item) => item.mappingList)
		);

		recommender.lodging.forEach(({ id }, index, arr) => {
			if (!baseItem.mappingList?.length) {
				pushIfNotExist(baseMappingList, PageTypeEnum.LODGING, id);
				return;
			}

			if (
				isLastFile &&
				!existingFileListMappingSet.has(`${PageTypeEnum.LODGING}-${id}`)
			) {
				duplicatedItems.push({
					...initBaseItem,
					id: randomId(),
					mappingList: [createMapping(PageTypeEnum.LODGING, id)],
				});
			}
		});
	}

	// Append empty mapping to all for consistency
	duplicatedItems.forEach((duplicatedItem) => {
		if (includes(item.refTableName, TARGET_PAGES)) {
			duplicatedItem.mappingList = duplicatedItem.mappingList.filter((ml) =>
				includes(ml.refTableName, parents)
			);
		}

		if (duplicatedItem.mappingList?.length > 1) return;

		if (
			!(item as TDocumentUploadDefineItem)?.id &&
			!duplicatedItem.mappingList?.length
		) {
			return (duplicatedItem.mappingList = [
				createMapping('' as PageTypeEnum, null),
			]);
		}

		const hasMapping = duplicatedItem.mappingList?.some(
			(ml) =>
				ml.refTableName === (item as TDocumentUploadDefineItem)?.refTableName &&
				ml.refTableId === (item as TDocumentUploadDefineItem)?.refTableId
		);
		if (hasMapping) {
			return (duplicatedItem.mappingList = [
				...duplicatedItem.mappingList,
				createMapping('' as PageTypeEnum, null),
			]);
		}
	});

	return duplicatedItems;
};
